import { Modal, Text } from '@mantine/core';
import dayjs from 'dayjs';
import { useState } from 'react';
import { DataTableColumn } from 'mantine-datatable';
import { Table } from 'src/common/components/table/Table';
import type { ProviderCoverage } from '../types';
import FeedDelayMessage from './FeedDelayMessage';
import HealthBadge from './HealthBadge';
import ProviderAction from './ProviderAction';

type ProviderFeedStatusTableProps = {
  providers: ProviderCoverage[];
  onMatchClick: (provider: ProviderCoverage) => void;
};

function formatLastSync(lastSync: string | null): string {
  if (!lastSync) return '—';

  const date = dayjs(lastSync);
  const now = dayjs();
  const hasTime = lastSync.includes('T');

  if (date.isSame(now, 'day')) return hasTime ? `Today, ${date.format('HH:mm')}` : 'Today';
  if (date.isSame(now.subtract(1, 'day'), 'day'))
    return hasTime ? `Yesterday, ${date.format('HH:mm')}` : 'Yesterday';

  const diffDays = now.diff(date, 'day');
  if (diffDays < 7) return `${diffDays} days ago`;

  return date.format('D MMM YYYY');
}

const columns: DataTableColumn<ProviderCoverage>[] = [
  {
    accessor: 'provider',
    title: 'Provider',
    render: ({ provider }) => (
      <Text size="sm" fw={500}>
        {provider}
      </Text>
    ),
  },
  {
    accessor: 'health',
    title: 'Status',
    render: ({ health }) => <HealthBadge status={health} />,
  },
  {
    accessor: 'unmatchedCount',
    title: 'Unmatched',
    render: ({ unmatchedCount }) => (
      <Text size="sm" c={unmatchedCount > 0 ? 'brand.6' : 'dimmed'}>
        {unmatchedCount > 0
          ? `${unmatchedCount} ${unmatchedCount === 1 ? 'product' : 'products'}`
          : '—'}
      </Text>
    ),
  },
  {
    accessor: 'lastSync',
    title: 'Last sync',
    render: ({ lastSync }) => (
      <Text size="sm" c="dimmed">
        {formatLastSync(lastSync)}
      </Text>
    ),
  },
];

function isDelayed(provider: ProviderCoverage): boolean {
  return provider.health === 'DELAYED' || provider.health === 'CRITICALLY_DELAYED';
}

export default function ProviderFeedStatusTable({
  providers,
  onMatchClick,
}: ProviderFeedStatusTableProps) {
  const [delayedProvider, setDelayedProvider] = useState<ProviderCoverage | null>(null);

  const handleRowClick = (provider: ProviderCoverage) => {
    if (isDelayed(provider)) {
      setDelayedProvider(provider);
    } else {
      onMatchClick(provider);
    }
  };

  const tableColumns: DataTableColumn<ProviderCoverage>[] = [
    ...columns,
    {
      accessor: 'actions',
      title: '',
      textAlign: 'right',
      render: provider => (
        <ProviderAction
          provider={provider}
          onMatchClick={onMatchClick}
          onGetUpdateClick={setDelayedProvider}
        />
      ),
    },
  ];

  return (
    <>
      <Table<ProviderCoverage>
        title="Provider feed status"
        columns={tableColumns}
        data={providers}
        isLoading={false}
        onRowClick={handleRowClick}
      />
      <Modal
        opened={delayedProvider !== null}
        onClose={() => setDelayedProvider(null)}
        title="Feed delay"
        centered
      >
        {delayedProvider && (
          <FeedDelayMessage
            providerName={delayedProvider.provider}
            lastSync={delayedProvider.lastSync}
          />
        )}
      </Modal>
    </>
  );
}
